/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import MessageHeader from "./MessageHeader";
import MessageBody from "./MessageBody";

import { useAppSelector } from "../../../utils/hooks";
import ConversationInfoDrawer from "../GroupInfo/ConversationInfoDrawer";
import SendMessage from "./SendMessage/SendMessage";
import IfUserBlocked from "./SendMessage/IfUserBlocked";

import { useTheme } from "../../../context/ThemeProvider";
import { ClipLoader } from "react-spinners";
import EmptyMessageList from "./EmptyMessageList";
import SearchMessageDrawer from "../GroupInfo/SearchMessageDrawer";
import DeleteSelectedMessage from "./SelectedMessageOption/DeleteSelectedMessage";
import ForwardSelectedMessage from "./SelectedMessageOption/ForwardSelectedMessage";
import { MessageBubbleContextMenuHost } from "./SelectedMessageOption/SelectedMessageOption";
import { useMessageList } from "../../../socket/useMessageList";
import { usePinnedCatalog } from "../../../hooks/usePinnedCatalog";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
} from "react";
import {
  appendNewerMessages,
  removeMessagesByIds,
  removePinnedMessagesByIds,
  updateMessageList,
} from "../../../store/Slices/MessageListSlice";
import {
  isPinnedCatalogAuthoritative,
  writePinnedCatalogDisk,
} from "../../../utils/fetchPinnedMessages";
import { updateMessageOptions } from "../../../store/Slices/MessageOptionsSlice";
import { updateSendMessageData } from "../../../store/Slices/SendMessageSlice";
import { useAppDispatch } from "../../../utils/hooks";

import { MessageListRes } from "../../../types/MessageListType";
import { UseQueryResult } from "react-query";
import scrollToMessage from "../../../utils/scrollToMessage";
import { socketInstance } from "../../../socket/socket";
import {
  clearUnSeenMessagesForChat,
} from "../../../store/Slices/UnSeenMessagesSlice";
import OnClickScrollToBottom from "./OnClickScrollToBottom";
import { messageListPayloadChatId } from "../../../utils/socketQueries";
import { useFile } from "../../../context/FileProvider";
import ChatFileDropOverlay from "../../../components/ChatFileDropOverlay";
import {
  dragPayloadLooksLikeMedia,
  isFileDragEvent,
  resolveDropKind,
  type ChatDropZone,
} from "../../../utils/chatDropFiles";
import toast from "react-hot-toast";
import { useTranslateText } from "../../../hooks/useTranslateText";
import { catchUpChatDifference } from "../../../utils/fetchChatDifference";
import { isRealMessage } from "../../../utils/messageListWindow";

export default function MessageList() {
  const MessageListArray = useAppSelector((state) => state.MessageList);
  const MessageOptions = useAppSelector((state) => state.MessageOptions);
  const currentConversationData = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const unSeenMessages = useAppSelector((state) => state.UnSeenMessages);
  const dispatch = useAppDispatch();
  const translate = useTranslateText();
  const appliedTipKey = useRef<string>("");
  const dragDepthRef = useRef(0);
  const { addChatAttachment } = useFile();
  const [fileDragActive, setFileDragActive] = useState(false);
  const [dropSplitZones, setDropSplitZones] = useState(false);
  const [dropHoverZone, setDropHoverZone] = useState<ChatDropZone | null>(null);
  // @ts-ignore
  const { theme } = useTheme();

  // Tip (latest) page only — older history uses keyset before_message_id
  const { data: messageListData }: UseQueryResult<MessageListRes, Error> =
    useMessageList(
      currentConversationData.chat_id,
      currentConversationData.user_id!,
      1,
    )!;

  // Pin bar SoT — dedicated catalog (disk warm + network), not tip window
  usePinnedCatalog(currentConversationData.chat_id);

  useEffect(() => {
    // Don't overwrite an around-jump / deep history window with tip cache
    if (MessageOptions.historyMode === "window") return;

    if (messageListData?.message_list?.Records?.length! > 0) {
      const payloadChatId = messageListPayloadChatId(messageListData);
      // Never paint another chat's tip into the active conversation
      if (
        payloadChatId != null &&
        Number(payloadChatId) !== Number(currentConversationData.chat_id)
      ) {
        return;
      }

      const tipKey = `${currentConversationData.chat_id}:${messageListData.message_list.Records[0]?.message_id}:${messageListData.message_list.Records.length}`;
      const isFreshChat =
        appliedTipKey.current.split(":")[0] !==
        String(currentConversationData.chat_id);

      // Tip page is always at the live edge — never poison Pagination.has_more_newer
      // (LoadMoreMessages used to treat that flag as "tip trimmed").
      const tipPayload = {
        ...messageListData!,
        message_list: {
          ...messageListData!.message_list,
          Pagination: {
            ...messageListData!.message_list.Pagination,
            has_more_newer: false,
          },
        },
      };
      dispatch(updateMessageList(tipPayload));

      // Tip embed can warm/confirm pins when authoritative
      if (
        isPinnedCatalogAuthoritative(tipPayload as any) &&
        tipPayload.pinned_messages?.Records
      ) {
        writePinnedCatalogDisk(
          currentConversationData.chat_id,
          tipPayload.pinned_messages.Records,
        );
      }

      const pagination = tipPayload.message_list?.Pagination;
      dispatch(
        updateMessageOptions({
          totalPages: Number(pagination?.total_pages || 0),
          isMessageLoading: false,
          isMoreMessageLoading: false,
          messageListAtTop: false,
          historyMode: "page",
          has_more_older:
            pagination?.has_more_older ??
            Number(pagination?.current_page) < Number(pagination?.total_pages),
          has_more_newer: false,
          currentPage: "1",
        }),
      );

      if (isFreshChat || appliedTipKey.current !== tipKey) {
        setTimeout(() => {
          scrollToMessage(
            messageListData?.message_list?.Records?.[0]?.message_id!,
            false,
            false,
          );
        }, 10);
      }
      appliedTipKey.current = tipKey;
    } else {
      if (currentConversationData.chat_id <= 0) {
        dispatch(
          updateMessageOptions({
            isMessageLoading: false,
          }),
        );
      } else if (currentConversationData.is_message_list_empty) {
        dispatch(
          updateMessageOptions({
            isMessageLoading: false,
          }),
        );
      }
    }
  }, [
    messageListData,
    currentConversationData.chat_id,
    currentConversationData.is_conversation_changed,
    currentConversationData.is_message_list_empty,
    MessageOptions.historyMode,
    dispatch,
  ]);

  useEffect(() => {
    appliedTipKey.current = "";
  }, [currentConversationData.chat_id]);

  // After tip paint, pull anything the socket missed while this chat was closed.
  const caughtUpChatRef = useRef(0);
  useEffect(() => {
    caughtUpChatRef.current = 0;
  }, [currentConversationData.chat_id]);

  useEffect(() => {
    const chatId = Number(currentConversationData.chat_id);
    if (!chatId || MessageOptions.historyMode === "window") return;
    if (caughtUpChatRef.current === chatId) return;
    const records = messageListData?.message_list?.Records;
    if (!records?.length) return;
    const payloadChatId = messageListPayloadChatId(messageListData);
    if (payloadChatId != null && Number(payloadChatId) !== chatId) return;

    const lastKnown = Math.max(
      0,
      ...records
        .filter(isRealMessage)
        .map((m) => Number(m.message_id))
        .filter((n) => n > 0),
    );
    if (!lastKnown) return;

    caughtUpChatRef.current = chatId;
    void catchUpChatDifference({
      chat_id: chatId,
      after_message_id: lastKnown,
      onPage: (page) => {
        if (caughtUpChatRef.current !== Number(page.chat_id)) return;
        if (page.deleted_message_ids?.length) {
          dispatch(removeMessagesByIds(page.deleted_message_ids));
          dispatch(removePinnedMessagesByIds(page.deleted_message_ids));
        }
        if (page.messages?.length) {
          dispatch(appendNewerMessages(page.messages));
        }
      },
    }).catch(() => {
      /* non-fatal */
    });
  }, [
    currentConversationData.chat_id,
    messageListData,
    MessageOptions.historyMode,
    dispatch,
  ]);

  // Batch seen when opening a chat (one event instead of N).
  useEffect(() => {
    const chatId = currentConversationData.chat_id;
    if (!chatId) return;
    const forChat = unSeenMessages.filter((e) => e.chat_id == chatId);
    if (forChat.length === 0) return;
    const upTo = Math.max(...forChat.map((e) => Number(e.message_id)));
    if (!upTo) return;
    socketInstance().emit("messages_seen", {
      chat_id: chatId,
      up_to_message_id: upTo,
      status: "seen",
    });
    dispatch(clearUnSeenMessagesForChat(chatId));
  }, [currentConversationData.chat_id]);

  const isBlocked =
    currentConversationData.is_group_blocked ||
    currentConversationData.other_user_blocked_you;
  const showForwardBar =
    MessageOptions.selectMessage && MessageOptions.forward_message;
  const showDeleteBar =
    MessageOptions.selectMessage && MessageOptions.delete_message;
  // Keep SendMessage mounted under select bars — unmounting remounts + focuses input → chat jumps
  const hideCompose = showForwardBar || showDeleteBar;
  const canDropFiles =
    !isBlocked &&
    !hideCompose &&
    (currentConversationData.chat_id > 0 ||
      currentConversationData.user_id > 0);

  const resetFileDrag = useCallback(() => {
    dragDepthRef.current = 0;
    setFileDragActive(false);
    setDropSplitZones(false);
    setDropHoverZone(null);
  }, []);

  const ingestDroppedFiles = useCallback(
    (files: File[], zone: ChatDropZone) => {
      if (files.length === 0) return;
      let added = 0;
      for (const f of files) {
        const k = resolveDropKind(f, zone);
        if (!k) {
          toast.error(translate("Unsupported file type"));
          continue;
        }
        const sizeMb = f.size / (1024 * 1024);
        if (sizeMb > 100) {
          toast.error(translate("File size is greater than 100 MB"), {
            duration: 3000,
          });
          continue;
        }
        addChatAttachment(f, k);
        added += 1;
      }
      if (added > 0) {
        dispatch(
          updateSendMessageData({
            showAttachmentOptions: false,
            message_type: "",
          }),
        );
      }
    },
    [addChatAttachment, dispatch, translate],
  );

  const onChatDragEnter = (e: ReactDragEvent) => {
    if (!canDropFiles || !isFileDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setDropSplitZones(dragPayloadLooksLikeMedia(e.dataTransfer));
    setFileDragActive(true);
  };

  const onChatDragLeave = (e: ReactDragEvent) => {
    if (!canDropFiles || !isFileDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) resetFileDrag();
  };

  const onChatDragOver = (e: ReactDragEvent) => {
    if (!canDropFiles || !isFileDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      e.dataTransfer.dropEffect = "copy";
    } catch {
      /* ignore */
    }
    setDropSplitZones(dragPayloadLooksLikeMedia(e.dataTransfer));
  };

  const onOverlayDrop = (zone: ChatDropZone, e: ReactDragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer?.files || []);
    resetFileDrag();
    if (!canDropFiles) return;
    ingestDroppedFiles(files, zone);
  };

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col">
      <MessageHeader />

      <div
        className="relative min-h-0 flex-1"
        onDragEnter={onChatDragEnter}
        onDragLeave={onChatDragLeave}
        onDragOver={onChatDragOver}
        onDrop={(e) => {
          if (!canDropFiles || !isFileDragEvent(e)) return;
          e.preventDefault();
          e.stopPropagation();
          // Fallback if drop misses zone hit-targets
          const zone: ChatDropZone = dropSplitZones
            ? dropHoverZone || "media"
            : "doc";
          onOverlayDrop(zone, e);
        }}
      >
        {MessageOptions.isMessageLoading ? (
          <div className="flex h-full min-h-0 flex-col items-center justify-center gap-y-2 px-14 pb-24 lg:py-6">
            <ClipLoader color={theme == "dark" ? "white" : "black"} />
          </div>
        ) : MessageListArray.message_list?.Records?.length === 0 ? (
          <EmptyMessageList />
        ) : (
          <MessageBody />
        )}

        {/* Slide up from under send plane */}
        {!MessageOptions.isMessageLoading &&
          (MessageListArray.message_list?.Records?.length || 0) > 0 && (
            <OnClickScrollToBottom />
          )}

        <div
          className={
            hideCompose ? "pointer-events-none invisible" : undefined
          }
          aria-hidden={hideCompose}
        >
          {isBlocked ? <IfUserBlocked /> : <SendMessage />}
        </div>
        {showDeleteBar ? <DeleteSelectedMessage /> : null}
        {showForwardBar ? <ForwardSelectedMessage /> : null}

        <ChatFileDropOverlay
          active={fileDragActive && canDropFiles}
          splitZones={dropSplitZones}
          hoverZone={dropHoverZone}
          onHoverZone={setDropHoverZone}
          onDropZone={onOverlayDrop}
        />
      </div>

      <ConversationInfoDrawer />
      <SearchMessageDrawer />
      {/* Single shared bubble ПКМ menu (DM + groups) — not per-message */}
      <MessageBubbleContextMenuHost />
    </div>
  );
}
